import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CustomerSupport.css";
import TicketDetailModal from "./TicketDetailModal";
import {
    customerSupportApi,
    PRIORITY_CHIP_CLS,
    PRIORITY_DOT,
    STATUS_CHIP_CLS,
    STATUS_DOT,
    PRIORITY_VALUES,
    STATUS_VALUES,
    TYPE_VALUES,
    SOURCE_VALUES,
    FIXDESK_TYPE_VALUES,
    FIXDESK_TYPE_DOT,
    SEVERITY_FILTER_VALUES,
    SEVERITY_DOT,
    SEVERITY_CHIP_CLS,
    SEVERITY_SHORT,
    initials,
    avatarGradient,
    relativeTime,
    slaText,
} from "./customerSupportApi";

const DEFAULT_PAGE_SIZE = 25;

const SLA_OPTS = [
    { value: "ok", label: "On Track", dot: "#4ade80" },
    { value: "warn", label: "At Risk (<2h)", dot: "#facc15" },
    { value: "danger", label: "Breached", dot: "#f87171" },
];
const SLA_LABEL = { ok: "On Track", warn: "At Risk", danger: "Breached" };

/* Categories are free-text in eMedHub but bounded by a fixed choice list
   (see emedhub/BE/support/constants.py CATEGORY_CHOICES). */
const CATEGORY_VALUES = [
    "Appointment Scheduling",
    "Billing / Payment",
    "Patient Records",
    "System / Performance",
    "Reports",
    "Queue Management",
    "IP Home",
    "Walkin",
    "Other",
];

const FILTER_LABELS = {
    status: "Status", priority: "Priority", category: "Category", agent: "Agent",
    tenant: "Organization", type: "Type", fixdesk_type: "FixDesk Type",
    severity: "Severity",
    hotfix: "Hot-fix", sla: "SLA", source: "Source", date: "Date",
};
const DATE_PRESETS = [
    { value: "today", label: "Today", days: 0 },
    { value: "yesterday", label: "Yesterday", days: 1 },
    { value: "7d", label: "Last 7 days", days: 7 },
    { value: "30d", label: "Last 30 days", days: 30 },
    { value: "90d", label: "Last 90 days", days: 90 },
    { value: "all", label: "All time", days: null },
];
const DATE_LABEL = { today: "Today", yesterday: "Yesterday", "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days", all: "All time", custom: "Custom" };

const showToast = (msg) => {
    let t = document.getElementById("cs-toast");
    if (!t) {
        t = document.createElement("div");
        t.id = "cs-toast";
        t.className = "cs-toast";
        document.body.appendChild(t);
    }
    t.textContent = msg;
    requestAnimationFrame(() => t.classList.add("show"));
    clearTimeout(t._hide);
    t._hide = setTimeout(() => t.classList.remove("show"), 2000);
};

const CheckSvg = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="20 6 9 17 4 12" /></svg>
);
const ChevronSvg = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
);

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgoStr = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
};

/* Small inline due-date chip with urgency-tinted color. */
const DueDateChip = ({ due, status }) => {
    if (!due) return null;
    const dueDate = new Date(`${due}T00:00:00`);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((dueDate - today) / (24 * 3600 * 1000));
    const isDone = status === "Resolved" || status === "Closed";
    let color, label;
    if (isDone) { color = "var(--cs-text-dim)"; label = `Due ${due}`; }
    else if (diffDays < 0) { color = "#f87171"; label = `Overdue ${Math.abs(diffDays)}d`; }
    else if (diffDays === 0) { color = "#fb923c"; label = `Due today`; }
    else if (diffDays <= 3) { color = "#facc15"; label = `Due in ${diffDays}d`; }
    else { color = "var(--cs-text-sec)"; label = `Due ${due}`; }
    return <span style={{ color, fontWeight: 500 }}>{label}</span>;
};

const dateRangeFor = (preset, custom) => {
    if (preset === "custom") return { from: custom.from || null, to: custom.to || null };
    if (preset === "all") return { from: null, to: null };
    // Yesterday is a single closed day, not "since yesterday".
    if (preset === "yesterday") return { from: daysAgoStr(1), to: daysAgoStr(1) };
    const p = DATE_PRESETS.find((x) => x.value === preset);
    if (!p) return { from: null, to: null };
    return { from: p.days === 0 ? todayStr() : daysAgoStr(p.days), to: todayStr() };
};

const CustomerSupportTickets = () => {
    const navigate = useNavigate();

    /* ── State: filters / paging ── */
    const [activeFilters, setActiveFilters] = useState({
        status: [], priority: [], category: [], agent: [], tenant: [],
        type: [], fixdesk_type: [], severity: [], hotfix: [], sla: [],
        source: [], date: "30d",
    });
    const [customDate, setCustomDate] = useState({ from: "", to: "" });
    const [searchTerm, setSearchTerm] = useState("");
    const [quickStat, setQuickStat] = useState("all");
    const [openDropdown, setOpenDropdown] = useState(null);
    const [showFilterRow, setShowFilterRow] = useState(true);
    const [sortLabel, setSortLabel] = useState("Newest");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [syncing, setSyncing] = useState(false);
    const [openTicket, setOpenTicket] = useState(null);   // currently-viewed row, or null

    /* ── Server state ── */
    const [tickets, setTickets] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, page_size: DEFAULT_PAGE_SIZE, total: 0, pages: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tenants, setTenants] = useState([]);
    const [servers, setServers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [counters, setCounters] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0, sla_breach: 0, unassigned: 0, untriaged: 0 });

    /* ── Map sort label → API sort key ── */
    const sortKey = useMemo(() => {
        switch (sortLabel) {
            case "Due (priority)": return "due_priority";
            case "Oldest": return "created_time";
            case "Priority ↓": return "priority";
            case "SLA ↑": return "resolution_due_at";
            case "Severity": return "severity";
            case "Slowest": return "tat";
            case "Newest":
            default: return "-created_time";
        }
    }, [sortLabel]);

    /* ── Build params for /tickets/ ── */
    const buildParams = useCallback(() => {
        const range = dateRangeFor(activeFilters.date, customDate);
        const params = {
            page, page_size: pageSize, sort: sortKey,
            status: activeFilters.status,
            priority: activeFilters.priority,
            category: activeFilters.category,
            type: activeFilters.type,
            fixdesk_type: activeFilters.fixdesk_type,
            severity: activeFilters.severity,
            source: activeFilters.source,
            sla: activeFilters.sla,
            tenant: activeFilters.tenant,
            agent: activeFilters.agent,
        };
        if (searchTerm.trim()) params.search = searchTerm.trim();
        if (range.from) params.date_from = range.from;
        if (range.to) params.date_to = range.to;
        // Hot-fix is a boolean flag: a single selection filters true/false;
        // selecting both (or neither) means "no filter".
        const hf = activeFilters.hotfix || [];
        if (hf.length === 1) params.hotfix = hf[0] === "Applied" ? "true" : "false";
        return params;
    }, [activeFilters, customDate, searchTerm, page, pageSize, sortKey]);

    /* ── Fetch list ── */
    const fetchTickets = useCallback(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        customerSupportApi
            .getTickets(buildParams())
            .then((res) => {
                if (cancelled) return;
                setTickets(res.data || []);
                setPagination(res.pagination || { page: 1, page_size: pageSize, total: (res.data || []).length, pages: 1 });
            })
            .catch((err) => {
                if (!cancelled) setError(err.message || "Failed to load tickets");
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [buildParams, pageSize]);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    /* ── Quick-stat counters come from /dashboard/ summary ──
       The cards honour the active filters (org, priority, type, date, …) so
       the numbers track what the user has narrowed to. We deliberately omit
       status / agent / sla here — those are the facets the cards themselves
       drive, so the per-status / unassigned / breach counts stay meaningful. */
    const fetchCounters = useCallback(() => {
        const range = dateRangeFor(activeFilters.date, customDate);
        const KNOWN_PRESETS = ["today", "7d", "30d", "90d", "all"];
        const params = {
            priority: activeFilters.priority,
            category: activeFilters.category,
            type: activeFilters.type,
            fixdesk_type: activeFilters.fixdesk_type,
            severity: activeFilters.severity,
            source: activeFilters.source,
            tenant: activeFilters.tenant,
        };
        if (KNOWN_PRESETS.includes(activeFilters.date)) params.preset = activeFilters.date;
        if (range.from) params.date_from = range.from;
        if (range.to) params.date_to = range.to;
        if (searchTerm.trim()) params.search = searchTerm.trim();
        const hf = activeFilters.hotfix || [];
        if (hf.length === 1) params.hotfix = hf[0] === "Applied" ? "true" : "false";

        customerSupportApi.getDashboard(params)
            .then((res) => {
                const s = (res?.data || res || {}).summary || {};
                setCounters({
                    total: s.total || 0,
                    open: s.open || 0,
                    in_progress: s.in_progress || 0,
                    resolved: s.resolved || 0,
                    sla_breach: s.sla_breach || 0,
                    unassigned: s.unassigned || 0,
                    untriaged: s.untriaged || 0,
                });
            })
            .catch(() => { /* non-fatal */ });
    }, [
        activeFilters.priority, activeFilters.category, activeFilters.type,
        activeFilters.fixdesk_type, activeFilters.severity, activeFilters.source,
        activeFilters.tenant, activeFilters.hotfix, activeFilters.date,
        customDate, searchTerm,
    ]);

    useEffect(() => { fetchCounters(); }, [fetchCounters]);

    /* ── Load tenants once for the org filter dropdown ──
       `servers` lists the eMedHub RDS instances FixDesk pulls from. With more
       than one, the same org name (and even the same database name) can appear
       on two servers, so labels get an environment suffix to stay distinct. */
    useEffect(() => {
        customerSupportApi.getTenants()
            .then((r) => {
                setTenants(r.data || []);
                setServers(r.servers || []);
            })
            .catch(() => { setTenants([]); setServers([]); });
    }, []);

    const multiServer = servers.length > 1;

    /* Label for one tenant in the Organization filter. Tenants are identified
       by their registry id, not their db_name — db names are only unique
       within a single server. */
    const tenantLabel = useCallback((t) => {
        if (!t) return "";
        const base = t.org_name || t.db_name;
        return multiServer && t.server_label ? `${base} · ${t.server_label}` : base;
    }, [multiServer]);

    const tenantById = useCallback(
        (v) => tenants.find((x) => String(x.id) === String(v)),
        [tenants],
    );

    /* ── Load employees once for the Agent filter dropdown ── */
    useEffect(() => {
        customerSupportApi.getEmployees()
            .then((rows) => setEmployees(rows || []))
            .catch(() => setEmployees([]));
    }, []);

    /* Map an agent id → display name for chips / labels. */
    const agentName = useCallback((id) => {
        if (id === "Unassigned") return "Unassigned";
        const e = employees.find((x) => String(x.id) === String(id));
        return e ? e.name : `Agent #${id}`;
    }, [employees]);

    /* ── Click outside closes dropdowns ── */
    useEffect(() => {
        const handler = (e) => {
            if (!e.target.closest(".dropdown") && !e.target.closest(".filter-select")) {
                setOpenDropdown(null);
            }
        };
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, []);

    /* ── Filter helpers ── */
    const toggleArrFilter = (key, value) => {
        setActiveFilters((prev) => {
            const arr = prev[key] || [];
            const next = arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
            return { ...prev, [key]: next };
        });
        setPage(1);
    };

    const handleQuickStat = (key) => {
        setQuickStat(key);
        setActiveFilters((prev) => {
            const next = { ...prev, status: [], agent: [], sla: [], severity: [] };
            if (key === "all") return next;
            if (key === "sla-breach") return { ...next, sla: ["danger"] };
            if (key === "untriaged") return { ...next, severity: ["Untriaged"] };
            if (key === "unassigned") return { ...next, agent: ["Unassigned"] };
            return { ...next, status: [key] };
        });
        setPage(1);
    };

    const activeChips = useMemo(() => {
        const chips = [];
        Object.entries(activeFilters).forEach(([key, val]) => {
            if (key === "date") {
                if (val && val !== "30d") {
                    chips.push({ key, raw: val, display: val === "custom" ? "custom range" : (DATE_LABEL[val] || val) });
                }
            } else if (Array.isArray(val)) {
                val.forEach((v) => {
                    let display = v;
                    if (key === "tenant") {
                        const t = tenantById(v);
                        display = t ? tenantLabel(t) : v;
                    }
                    if (key === "agent") display = agentName(v);
                    if (key === "sla") display = SLA_LABEL[v] || v;
                    chips.push({ key, raw: v, display });
                });
            }
        });
        return chips;
    }, [activeFilters, tenantById, tenantLabel, agentName]);

    const removeChip = (key, raw) => {
        if (key === "date") {
            setActiveFilters((p) => ({ ...p, date: "30d" }));
            setCustomDate({ from: "", to: "" });
        } else {
            setActiveFilters((p) => ({ ...p, [key]: p[key].filter((v) => String(v) !== String(raw)) }));
        }
        setPage(1);
    };

    const clearAll = () => {
        setActiveFilters({
            status: [], priority: [], category: [], agent: [], tenant: [],
            type: [], fixdesk_type: [], severity: [], hotfix: [], sla: [],
            source: [], date: "30d",
        });
        setCustomDate({ from: "", to: "" });
        setSearchTerm("");
        setQuickStat("all");
        setPage(1);
    };

    const filterValueLabel = (key) => {
        if (key === "date") {
            const v = activeFilters.date;
            return v === "custom" ? "Custom" : (DATE_LABEL[v] || "Last 30d");
        }
        const arr = activeFilters[key];
        if (!arr || arr.length === 0) return "All";
        if (arr.length === 1) {
            if (key === "tenant") {
                const t = tenantById(arr[0]);
                return t ? tenantLabel(t) : arr[0];
            }
            if (key === "agent") return agentName(arr[0]);
            if (key === "sla") return SLA_LABEL[arr[0]] || arr[0];
            return arr[0];
        }
        return `${arr.length} selected`;
    };

    const isFilterActive = (key) => {
        if (key === "date") return activeFilters.date !== "30d";
        return (activeFilters[key] || []).length > 0;
    };

    /* ── Sort options + popover state ── */
    const SORT_OPTIONS = [
        { value: "Newest", description: "Newest first" },
        { value: "Due (priority)", description: "Overdue → today → upcoming → undated" },
        { value: "Oldest", description: "Oldest first" },
        { value: "Priority ↓", description: "Critical first" },
        { value: "SLA ↑", description: "Soonest resolution deadline first" },
        { value: "Severity", description: "P0 first, untriaged last" },
        { value: "Slowest", description: "Most support time spent first" },
    ];
    const [showSortDD, setShowSortDD] = useState(false);
    const pickSort = (value) => {
        if (value !== sortLabel) {
            setSortLabel(value);
            setPage(1);
        }
        setShowSortDD(false);
    };

    /* Click outside the sort popover closes it. */
    useEffect(() => {
        const handler = (e) => {
            if (!e.target.closest("[data-sort-popover]")) setShowSortDD(false);
        };
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, []);

    /* ── Manual sync trigger ── */
    const handleSync = async () => {
        if (syncing) return;
        setSyncing(true);
        try {
            const res = await customerSupportApi.triggerSync({ refresh_registry: true });
            showToast(res.message || "Sync complete");
            fetchTickets();
            fetchCounters();
        } catch (e) {
            showToast(e.message || "Sync failed");
        } finally {
            setSyncing(false);
        }
    };

    /* CSV export — uses the same filter params as the visible list. */
    const [exporting, setExporting] = useState(false);
    const handleExport = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            // Strip pagination keys; the export endpoint dumps everything matching.
            const { page: _p, page_size: _ps, ...exportParams } = buildParams();
            showToast(`Exporting ${pagination.total} ticket${pagination.total === 1 ? "" : "s"}…`);
            const blob = await customerSupportApi.exportTickets(exportParams);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `tickets-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            showToast(`Exported ${pagination.total} ticket${pagination.total === 1 ? "" : "s"}`);
        } catch (e) {
            showToast(e.message || "Export failed");
        } finally {
            setExporting(false);
        }
    };

    /* ── Render dropdowns ── */
    const renderMultiDropdown = (key, opts) => {
        if (openDropdown !== key) return null;
        return (
            <div className="dropdown show" onClick={(e) => e.stopPropagation()}>
                {opts.map((opt, i) => {
                    if (opt.divider) return <div key={`d${i}`} className="dd-divider"></div>;
                    const selected = (activeFilters[key] || []).map(String).includes(String(opt.value));
                    return (
                        <div key={opt.value} className={`dd-option ${selected ? "selected" : ""}`} onClick={() => toggleArrFilter(key, String(opt.value))}>
                            <div className="dd-check"><CheckSvg /></div>
                            {opt.dot && <span className="dot" style={{ background: opt.dot }}></span>}
                            <span className="dd-label" style={opt.dim ? { color: "#fb923c" } : undefined}>{opt.label || opt.value}</span>
                            {opt.count != null && <span className="dd-count">{opt.count}</span>}
                        </div>
                    );
                })}
            </div>
        );
    };

    /* ── Quick stat strip ── */
    const QUICK_STATS = [
        { key: "all", label: "All Tickets", num: counters.total, dot: null, cls: "" },
        { key: "Open", label: "Open", num: counters.open, dot: "#3b82f6", cls: "" },
        { key: "In Progress", label: "In Progress", num: counters.in_progress, dot: "#facc15", cls: "" },
        { key: "Resolved", label: "Resolved", num: counters.resolved, dot: "#4ade80", cls: "" },
        { key: "sla-breach", label: "Past Target", num: counters.sla_breach, dot: null, cls: "danger" },
        // No severity means no clock is running against the ticket, which
        // quietly invalidates every other number on this strip — so it gets a
        // card of its own rather than hiding inside "All Tickets".
        { key: "untriaged", label: "No Severity", num: counters.untriaged, dot: null, cls: "warn" },
        { key: "unassigned", label: "Unassigned", num: counters.unassigned, dot: null, cls: "warn" },
    ];

    return (
        <div className="cs-page">
            {/* Page header */}
            <div className="page-hdr">
                <div>
                    <div className="page-crumb">
                        Customer Support <span className="sep">›</span> <span className="crumb-current">All Tickets</span>
                    </div>
                    <div className="page-title">All Tickets</div>
                </div>
                <div style={{ marginLeft: "auto" }}></div>
                <span className="live">
                    <span className="live-dot"></span>
                    Live
                </span>
                <button
                    className="filter-btn"
                    onClick={() => navigate("/customer-support/sla")}
                    title="Edit the turnaround target for each severity tier"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                    Targets
                </button>
                <button className="filter-btn" onClick={handleExport} disabled={exporting} title="Download the current filtered list as CSV">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                    {exporting ? "Exporting…" : "Export"}
                </button>
                <button className="filter-btn primary" onClick={handleSync} disabled={syncing} title="Pull new tickets from every tenant DB">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" /></svg>
                    {syncing ? "Syncing…" : "Sync from eMedHub"}
                </button>
            </div>

            {/* Quick stat strip */}
            <div className="qstats">
                {QUICK_STATS.map((s) => (
                    <div key={s.key} className={`qstat ${quickStat === s.key ? "active" : ""}`} onClick={() => handleQuickStat(s.key)}>
                        <div className={`qstat-num ${s.cls}`}>
                            {s.dot && <span className="dot" style={{ background: s.dot }}></span>}{s.num}
                        </div>
                        <div className="qstat-lbl">{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Filter bar */}
            <div className="filter-bar">
                <div className="filter-top">
                    <div className="filter-search">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                        <input
                            type="text"
                            placeholder="Search by ticket code, subject, customer, submitter, or agent…"
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                        />
                    </div>
                    <button className={`filter-btn ${activeChips.length > 0 ? "has-count" : ""}`} onClick={() => setShowFilterRow((v) => !v)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
                        Filters
                        {activeChips.length > 0 && <span className="ct">{activeChips.length}</span>}
                    </button>
                    <button className="filter-btn" onClick={() => showToast("Saved views — coming soon")}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
                        Saved Views
                    </button>
                </div>

                {showFilterRow && (
                    <div className="filter-row">
                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("status") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "status" ? null : "status"); }}>
                                <span className="lbl-dim">Status:</span> <span className="val">{filterValueLabel("status")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("status", STATUS_VALUES.map((v) => ({ value: v, label: v, dot: STATUS_DOT[v] })))}
                        </div>

                        {/* Type sits 2nd — it carries the "Support" option. */}
                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("type") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "type" ? null : "type"); }}>
                                <span className="lbl-dim">Type:</span> <span className="val">{filterValueLabel("type")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("type", TYPE_VALUES.map((v) => ({ value: v, label: v })))}
                        </div>

                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("priority") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "priority" ? null : "priority"); }}>
                                <span className="lbl-dim">Priority:</span> <span className="val">{filterValueLabel("priority")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("priority", PRIORITY_VALUES.map((v) => ({ value: v, label: v, dot: PRIORITY_DOT[v] })))}
                        </div>

                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("category") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "category" ? null : "category"); }}>
                                <span className="lbl-dim">Category:</span> <span className="val">{filterValueLabel("category")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("category", CATEGORY_VALUES.map((v) => ({ value: v, label: v })))}
                        </div>

                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("agent") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "agent" ? null : "agent"); }}>
                                <span className="lbl-dim">Agent:</span> <span className="val">{filterValueLabel("agent")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("agent", [
                                { value: "Unassigned", label: "Unassigned", dim: true },
                                ...(employees.length ? [{ divider: true }] : []),
                                ...employees.map((e) => ({ value: String(e.id), label: e.name })),
                            ])}
                        </div>

                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("tenant") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "tenant" ? null : "tenant"); }}>
                                <span className="lbl-dim">Organization:</span> <span className="val">{filterValueLabel("tenant")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("tenant", tenants.map((t) => ({ value: String(t.id), label: tenantLabel(t) })))}
                        </div>

                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("fixdesk_type") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "fixdesk_type" ? null : "fixdesk_type"); }}>
                                <span className="lbl-dim">FixDesk Type:</span> <span className="val">{filterValueLabel("fixdesk_type")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("fixdesk_type", FIXDESK_TYPE_VALUES.map((v) => ({ value: v, label: v, dot: FIXDESK_TYPE_DOT[v] })))}
                        </div>

                        {/* Severity — FixDesk's own tier, and the only field a
                            turnaround target runs against. Sits next to
                            Priority deliberately: seeing the two side by side
                            is how you notice a customer-filed Critical that
                            triage put at P3. */}
                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("severity") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "severity" ? null : "severity"); }}>
                                <span className="lbl-dim">Severity:</span> <span className="val">{filterValueLabel("severity")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("severity", SEVERITY_FILTER_VALUES.map((v) => ({
                                value: v,
                                label: SEVERITY_SHORT[v] ? `${v} · ${SEVERITY_SHORT[v]}` : v,
                                dot: SEVERITY_DOT[v],
                            })))}
                        </div>

                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("hotfix") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "hotfix" ? null : "hotfix"); }}>
                                <span className="lbl-dim">Hot-fix:</span> <span className="val">{filterValueLabel("hotfix")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("hotfix", [
                                { value: "Applied", label: "Hot-fix applied", dot: "#facc15" },
                                { value: "Not applied", label: "No hot-fix" },
                            ])}
                        </div>

                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("sla") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "sla" ? null : "sla"); }}>
                                <span className="lbl-dim">SLA:</span> <span className="val">{filterValueLabel("sla")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("sla", SLA_OPTS)}
                        </div>

                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("date") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "date" ? null : "date"); }}>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: .8 }}><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                                <span className="lbl-dim">Date:</span> <span className="val">{filterValueLabel("date")}</span>
                                <ChevronSvg />
                            </button>
                            {openDropdown === "date" && (
                                <div className="dropdown dd-date show" onClick={(e) => e.stopPropagation()}>
                                    <div className="dd-header">Quick ranges</div>
                                    <div className="dd-date-presets">
                                        {DATE_PRESETS.map((p) => (
                                            <div key={p.value} className={`dd-option ${activeFilters.date === p.value ? "selected" : ""}`} onClick={() => { setActiveFilters((prev) => ({ ...prev, date: p.value })); setCustomDate({ from: "", to: "" }); setOpenDropdown(null); setPage(1); }}>
                                                <span className="dd-label">{p.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="dd-divider"></div>
                                    <div className="dd-header">Custom range</div>
                                    <div className="dd-date-inputs">
                                        <div>
                                            <label>From</label>
                                            <input type="date" value={customDate.from} onChange={(e) => { setCustomDate({ ...customDate, from: e.target.value }); setActiveFilters((p) => ({ ...p, date: "custom" })); setPage(1); }} />
                                        </div>
                                        <div>
                                            <label>To</label>
                                            <input type="date" value={customDate.to} onChange={(e) => { setCustomDate({ ...customDate, to: e.target.value }); setActiveFilters((p) => ({ ...p, date: "custom" })); setPage(1); }} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="filter-group">
                            <button className={`filter-select ${isFilterActive("source") ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); setOpenDropdown(openDropdown === "source" ? null : "source"); }}>
                                <span className="lbl-dim">Source:</span> <span className="val">{filterValueLabel("source")}</span>
                                <ChevronSvg />
                            </button>
                            {renderMultiDropdown("source", SOURCE_VALUES.map((v) => ({ value: v, label: v })))}
                        </div>
                    </div>
                )}

                {activeChips.length > 0 && (
                    <div className="active-filters">
                        <span className="af-label">Active filters:</span>
                        {activeChips.map((c, i) => (
                            <span key={`${c.key}-${c.raw}-${i}`} className="af-chip">
                                <span className="af-key">{FILTER_LABELS[c.key]}:</span> {c.display}
                                <button className="af-x" onClick={() => removeChip(c.key, c.raw)}>
                                    <svg viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                                </button>
                            </span>
                        ))}
                        <button className="af-clear-all" onClick={clearAll}>Clear all</button>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="table-panel">
                <div className="table-toolbar">
                    <svg viewBox="0 0 24 24" fill="none" stroke="var(--cs-accent-l)" strokeWidth="2" width="16" height="16"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                    <span className="panel-title">Tickets</span>
                    <span className="panel-sub" style={{ marginLeft: 0 }}>
                        {loading ? "Loading…" : `Showing ${tickets.length} of ${pagination.total}`}
                    </span>
                    <div className="toolbar-spacer">
                        {/* Sort: single-select popover. Clicking opens a small
                            list of options with a radio-style indicator beside
                            the currently-active one. */}
                        <div data-sort-popover style={{ position: "relative" }}>
                            <button
                                className="t-action"
                                onClick={(e) => { e.stopPropagation(); setShowSortDD((v) => !v); }}
                            >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M6 12h12M10 18h4" /></svg>
                                Sort: <span style={{ color: "var(--cs-text)", fontWeight: 600 }}>{sortLabel}</span>
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11" style={{ opacity: .6, marginLeft: 4 }}><polyline points="6 9 12 15 18 9" /></svg>
                            </button>
                            {showSortDD && (
                                <div className="dropdown show" style={{ right: 0, left: "auto", minWidth: 240 }} onClick={(e) => e.stopPropagation()}>
                                    {SORT_OPTIONS.map((opt) => {
                                        const selected = opt.value === sortLabel;
                                        return (
                                            <div
                                                key={opt.value}
                                                className={`dd-option ${selected ? "selected" : ""}`}
                                                onClick={() => pickSort(opt.value)}
                                                role="option"
                                                aria-selected={selected}
                                            >
                                                {/* Radio indicator — circle outline when unselected, filled when selected. */}
                                                <div style={{
                                                    width: 14, height: 14, borderRadius: "50%",
                                                    border: `1.5px solid ${selected ? "var(--cs-accent)" : "var(--cs-border-hi)"}`,
                                                    background: selected ? "var(--cs-accent)" : "var(--cs-bg)",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    flexShrink: 0,
                                                }}>
                                                    {selected && (
                                                        <div style={{
                                                            width: 5, height: 5, borderRadius: "50%",
                                                            background: "#fff",
                                                        }} />
                                                    )}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div className="dd-label">{opt.value}</div>
                                                    <div style={{ fontSize: 10.5, color: "var(--cs-text-dim)", marginTop: 1 }}>
                                                        {opt.description}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <button className="t-action" onClick={() => showToast("Column settings — coming soon")}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" /></svg>
                            Columns
                        </button>
                        <button className="t-action" onClick={() => { fetchTickets(); fetchCounters(); }}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" /></svg>
                            Refresh
                        </button>
                    </div>
                </div>

                <div className="tkt-table">
                    <div className="tkt-head">
                        <span>TICKET</span>
                        <span>FIXDESK TYPE</span>
                        <span>CUSTOMER</span>
                        <span>SEVERITY</span>
                        <span>PRIORITY</span>
                        <span>STATUS</span>
                        <span>AGENT</span>
                        <span>TURNAROUND</span>
                    </div>

                    {tickets.map((t) => {
                        const agentName = t.assigned_to_name || "Unassigned";
                        const isUnassigned = !t.assigned_to;
                        const sla = slaText(t);
                        return (
                            <div
                                key={t.id}
                                className="tkt-row"
                                onClick={() => setOpenTicket(t)}
                                style={{ cursor: "pointer" }}
                            >
                                <div className="tkt-main">
                                    <div className="tkt-main-top">
                                        <span className="tkt-id">{t.ticket_code}</span>
                                        {t.reopen_count > 0 && (
                                            <span className="tkt-flag tkt-flag-reopen" title={`Reopened ${t.reopen_count} time${t.reopen_count === 1 ? "" : "s"}`}>
                                                ↻ Reopened
                                            </span>
                                        )}
                                        {t.fixdesk_moved_to_requirements && (
                                            <span className="tkt-flag tkt-flag-moved" title="Moved to client requirements">
                                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="9" height="9"><polyline points="20 6 9 17 4 12" /></svg>
                                                Moved to requirements
                                            </span>
                                        )}
                                        {t.fixdesk_moved_to_sprint && (
                                            <span className="tkt-flag tkt-flag-sprint" title="Moved to a sprint">
                                                ⮕ Sprint
                                            </span>
                                        )}
                                        {t.fixdesk_hotfix_applied && (
                                            <span className="tkt-flag tkt-flag-hotfix" title="Hot-fix applied">
                                                ⚡ Hot-fix
                                            </span>
                                        )}
                                    </div>
                                    <div className="tkt-subj" title={t.subject}>{t.subject}</div>
                                    {t.summary && (
                                        <div className="tkt-summary" title={t.summary}>{t.summary}</div>
                                    )}
                                    <div className="tkt-sub-meta">
                                        {t.type} · {relativeTime(t.source_created_at)} · {t.source}
                                    </div>
                                </div>
                                <div className="fd-type-cell">
                                    {t.fixdesk_type ? (
                                        <span className="fd-type-chip">
                                            <span className="chip-dot" style={{ background: FIXDESK_TYPE_DOT[t.fixdesk_type] || "#94a3b8" }}></span>
                                            {t.fixdesk_type}
                                        </span>
                                    ) : (
                                        <span className="tkt-empty">Not set</span>
                                    )}
                                </div>
                                <div className="cust-cell">
                                    <span className="cust-name">{t.customer_name}</span>
                                    {t.customer_dept && <span className="cust-meta">{t.customer_dept}</span>}
                                </div>
                                <div className="sev-cell">
                                    {t.severity ? (
                                        <span
                                            className={`sev-chip ${SEVERITY_CHIP_CLS[t.severity] || ""}`}
                                            title={`${t.severity} — ${SEVERITY_SHORT[t.severity] || ""}${t.sla_target ? ` · target ${t.sla_target.resolution_display}` : ""}`}
                                        >
                                            <span className="chip-dot" style={{ background: SEVERITY_DOT[t.severity] }}></span>
                                            {t.severity}
                                        </span>
                                    ) : (
                                        // Not a neutral empty state — an untriaged
                                        // ticket has no clock running against it.
                                        <span className="sev-chip sev-none" title="No severity set — no turnaround target applies to this ticket">
                                            Set severity
                                        </span>
                                    )}
                                    {t.severity_source === "auto" && (
                                        <span className="sev-auto" title="Seeded from the customer's priority — nobody has triaged this by hand yet">auto</span>
                                    )}
                                </div>
                                <span className={`pr-chip ${PRIORITY_CHIP_CLS[t.priority]}`}>
                                    <span className="chip-dot" style={{ background: PRIORITY_DOT[t.priority] }}></span>{t.priority}
                                </span>
                                <span className={`chip ${STATUS_CHIP_CLS[t.status]}`}>
                                    <span className="chip-dot" style={{ background: STATUS_DOT[t.status] }}></span>{t.status}
                                </span>
                                <div className="agent-pill">
                                    <div className={`agent-avatar ${isUnassigned ? "unassigned" : ""}`}
                                        style={isUnassigned ? undefined : { background: avatarGradient(t.assigned_to || agentName) }}>
                                        {isUnassigned ? "?" : initials(agentName)}
                                    </div>
                                    <span className="agent-name">{agentName}</span>
                                </div>
                                <div className="tat-cell">
                                    <span className={`sla-pill sla-${t.sla_status || "ok"}`}>
                                        {t.sla_status === "danger" ? (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /></svg>
                                        ) : (sla === "Done" ? (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12" /></svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                        ))}
                                        {sla}
                                    </span>

                                    {/* Support-owned time against the tier's target.
                                        Working hours only, with time spent waiting on
                                        the customer removed — the wall clock lives in
                                        the detail modal, because holding anyone to a
                                        number that counts Sundays doesn't work. */}
                                    {t.active_tat_display && (
                                        <span
                                            className={`tat-val ${t.resolution_breached ? "over" : ""}`}
                                            title={
                                                `Support time: ${t.active_tat_display}` +
                                                (t.sla_target ? ` of ${t.sla_target.resolution_display}` : " (no target — set a severity)") +
                                                (t.tat_display ? ` · ${t.tat_display} elapsed overall` : "") +
                                                (t.waiting_display ? ` · ${t.waiting_display} waiting on customer` : "")
                                            }
                                        >
                                            {t.active_tat_display}
                                            {t.sla_target && <span className="tat-target"> / {t.sla_target.resolution_display}</span>}
                                            {!t.tat_resolved && <span className="tat-ongoing">·</span>}
                                        </span>
                                    )}

                                    {/* Only flagged when it's the FRT that slipped —
                                        the pill above already covers resolution, and
                                        two identical red badges say nothing extra. */}
                                    {t.frt_breached && !t.resolution_breached && (
                                        <span className="tkt-flag tkt-flag-frt" title={`First reply took ${t.frt_display || "too long"}`}>
                                            Slow first reply
                                        </span>
                                    )}

                                    {t.due_date && <DueDateChip due={t.due_date} status={t.status} />}
                                </div>
                            </div>
                        );
                    })}

                    {!loading && tickets.length === 0 && (
                        <div className="empty-results show">
                            <div className="empty-results-icon">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                            </div>
                            <div className="empty-results-title">
                                {error ? "Failed to load tickets" : "No tickets match your filters"}
                            </div>
                            <div className="empty-results-sub">
                                {error ? error : "Try adjusting your search or clearing some filters"}
                            </div>
                        </div>
                    )}

                    {loading && tickets.length === 0 && (
                        <div className="empty-results show">
                            <div className="empty-results-title">Loading tickets…</div>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                <div className="pagination">
                    <span className="pg-info">
                        Showing <strong>{tickets.length === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1}–{(pagination.page - 1) * pagination.page_size + tickets.length}</strong> of <strong>{pagination.total}</strong> tickets
                    </span>
                    <div className="pg-size">
                        Rows:
                        <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                            <option>10</option>
                            <option>25</option>
                            <option>50</option>
                            <option>100</option>
                        </select>
                    </div>
                    <div className="pg-controls">
                        <button className="pg-btn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} title="Previous">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
                        </button>
                        {Array.from({ length: Math.min(5, pagination.pages || 1) }, (_, i) => i + 1).map((n) => (
                            <button key={n} className={`pg-btn ${n === page ? "active" : ""}`} onClick={() => setPage(n)}>{n}</button>
                        ))}
                        {pagination.pages > 5 && <span className="pg-ellipsis">…</span>}
                        {pagination.pages > 5 && <button className="pg-btn" onClick={() => setPage(pagination.pages)}>{pagination.pages}</button>}
                        <button className="pg-btn" disabled={page >= (pagination.pages || 1)} onClick={() => setPage((p) => Math.min(pagination.pages || 1, p + 1))} title="Next">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
                        </button>
                    </div>
                </div>
            </div>

            <TicketDetailModal
                open={!!openTicket}
                ticket={openTicket}
                onClose={() => setOpenTicket(null)}
                onSaved={(updated) => {
                    if (!updated) { fetchTickets(); fetchCounters(); return; }
                    // Patch the row in-place so the list reflects the change
                    // instantly without waiting for a full refetch.
                    setTickets((rows) => rows.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
                    fetchCounters();
                }}
            />
        </div>
    );
};

export default CustomerSupportTickets;
